import { makeAutoObservable } from 'mobx';
import API, { IEntityContentItem, IGlobalLocations, IUserConfigPackage } from '../API';
import { EntityNavigatorState } from '../components/controls/EntityNavigator';
import OutlookLocationContentsViewerState, {
	IContextCommands,
	IOutlookAppCommands
} from '../state/CorporateArchiveStates/LegalEntityDocumentsStateOutlook';
import { DocumentSearchResultsState } from '../components/controls/documents/DocumentSearchResults';
import { DocumentViewerDialogState } from '../components/controls/documents/DocumentViewerDialog';

export class CombinedOutlookLocationViewer {
	public userConfig: IUserConfigPackage;

	public loading: boolean = false;
	public visible: boolean = false;

	public entityNavigators: EntityNavigatorState[] = [];

	public searchText: string = '';
	public allItems: IGlobalLocations | null = null;
	public orphanResults: OutlookLocationContentsViewerState;
	public siteResults: EntityNavigatorState;

	public commands: IOutlookAppCommands;
	public ctxCommands: IContextCommands;
	public docResults: DocumentSearchResultsState;
	public docViewerDlg: DocumentViewerDialogState;

	constructor(
		userConfig: IUserConfigPackage,
		commands: IOutlookAppCommands,
		ctxCommands: IContextCommands
	) {
		makeAutoObservable(this);
		this.userConfig = userConfig;
		this.commands = commands;
		this.ctxCommands = ctxCommands;
		this.orphanResults = new OutlookLocationContentsViewerState(
			'',
			'',
			'',
			'',
			'',
			this.userConfig,
			commands,
			ctxCommands,
		);
		this.siteResults = new EntityNavigatorState(
			userConfig,
			'',
			[],
			commands,
			ctxCommands
		);

		this.docViewerDlg = new DocumentViewerDialogState(userConfig);
		this.docResults = new DocumentSearchResultsState(userConfig, true, (resultItem) => {

			//Show the document viewer dialog when an item is selected
			this.docViewerDlg.show(
				resultItem.webUrl,
				resultItem.name,
			);
		}, () => {
			if (this.docResults.results) {
				this.orphanResults.setResult({
					documents: this.docResults.results.items as any as IEntityContentItem[],
					folders: [],
					webUrl: ''
				});
			}
		});

	}

	public clear() {
		this.setSearchText('');
	}

	public setSearchText(searchText: string) {
		this.searchText = searchText;
		if (searchText) {
			this.loading = true;
			this.visible = true;

			//Kick off a search
			this.startSearch(searchText);
			this.docResults.setSearchText(searchText);
		} else {
			this.loading = false;
			this.visible = false;
			this.allItems = null;
			this.entityNavigators = [];
		}
	}

	private async startSearch(searchText: string): Promise<void> {
		var api = new API(await this.userConfig.getToken());
		var results = await api.combinedSearch(
			searchText
		);
		if (searchText === this.searchText) {
			this.setResults(results);
		}
	}

	public setLoading(isLoading: boolean) {
		this.loading = isLoading;
	}


	public findMatchingItem(entityType: string, entityId: string, url: string): OutlookLocationContentsViewerState | undefined {
		let matchingEntityNav = this.entityNavigators.find(e => e.entityType === entityType);
		if (matchingEntityNav?.documentViewers) {
			let match = matchingEntityNav.documentViewers.find((m) => m.entityId === entityId);
			if (match) {
				return match;
			}
		}

		if (url && this.siteResults?.documentViewers) {
			let siteMatch = this.siteResults.documentViewers.find((m) => m.url === url);
			if (siteMatch) {
				return siteMatch;
			}
		}

		return undefined;
	}

	public remove(state: OutlookLocationContentsViewerState) {
		this.entityNavigators.forEach(en => en.remove(state));
		this.siteResults.remove(state);
	}

	public contains(entityType: string, entityId: string, url: string): boolean {

		if (entityType && entityId && this.allItems?.entities) {

			let matchingEntityType = this.allItems.entities.find(
				(e) => e.entityType === entityType,
			);
			if (matchingEntityType) {
				let match = matchingEntityType.entities.items.find((m) => m.Id === entityId);
				return !!match;
			}
		} else if (url && this.allItems?.sites) {
			return !!this.allItems.sites.find((m) => m.webUrl === url);
		}

		return false;
	}

	public setResults(results: IGlobalLocations) {
		this.loading = false;
		this.allItems = results;

		this.entityNavigators = this.allItems.entities.map(
			(r) =>
				new EntityNavigatorState(
					this.userConfig,
					r.entityType,
					r.entities.items,
					this.commands,
					this.ctxCommands
				),
		);

		let siteDocViewers = this.allItems.sites.map(
			(s) =>
				new OutlookLocationContentsViewerState(
					s.displayName,
					s.webUrl,
					'',
					'',
					'',
					this.userConfig,
					this.commands,
					this.ctxCommands
				),
		);
		this.siteResults.setViewers(siteDocViewers);

		//Auto expand - if there's a single entity navigator, then expand it
		if (this.entityNavigators.length === 1) {
			this.entityNavigators[0].load().then((e) => {
				if (
					this.entityNavigators &&
					this.entityNavigators[0] &&
					this.entityNavigators[0].documentViewers &&
					this.entityNavigators[0].documentViewers?.length === 1
				) {
					this.entityNavigators[0].documentViewers[0].lineItem.toggleExpanded(
						true,
					);
				}
			});
		}
	}
}
